import type {
  GetFeatureFlagsResponseDto,
  GetFeatureFlagDto,
  CreateFeatureFlagDto,
  UpdateFeatureFlagDto,
  CreateFeatureFlagResponseDto,
  UpdateFeatureFlagResponseDto,
  DeleteFeatureFlagResponseDto,
  GetFeatureFlagScopesResponseDto,
  CreateFeatureFlagScopeResponseDto,
  UpdateFeatureFlagScopeResponseDto,
  DeleteFeatureFlagScopeResponseDto,
} from "@server/routes/feature-flags/dto";
import { getApiUrl } from "../config";

// Feature Flag API functions
export const getFeatureFlags = async (): Promise<GetFeatureFlagsResponseDto> => {
  const response = await fetch(getApiUrl("/api/feature-flags"), {
    credentials: 'include',
  });
  
  if (!response.ok) {
    throw new Error('Failed to fetch feature flags');
  }

  return response.json();
};

export const getFeatureFlagById = async (id: number): Promise<GetFeatureFlagDto> => {
  const response = await fetch(getApiUrl(`/api/feature-flags/${id}`), {
    credentials: 'include',
  });
  
  if (!response.ok) {
    throw new Error('Failed to fetch feature flag');
  }

  return response.json();
};

export const createFeatureFlag = async (data: CreateFeatureFlagDto): Promise<CreateFeatureFlagResponseDto> => {
  const response = await fetch(getApiUrl("/api/feature-flags"), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error('Failed to create feature flag');
  }

  return response.json();
};

export const updateFeatureFlag = async (id: number, data: UpdateFeatureFlagDto): Promise<UpdateFeatureFlagResponseDto> => {
  const response = await fetch(getApiUrl(`/api/feature-flags/${id}`), {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error('Failed to update feature flag');
  }

  return response.json();
};

export const deleteFeatureFlag = async (id: number): Promise<DeleteFeatureFlagResponseDto> => {
  const response = await fetch(getApiUrl(`/api/feature-flags/${id}`), {
    method: 'DELETE',
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to delete feature flag');
  }

  return response.json();
};

// Feature Flag Scope API functions
export const getFeatureFlagScopes = async (flagId: number): Promise<GetFeatureFlagScopesResponseDto> => {
  const response = await fetch(getApiUrl(`/api/feature-flags/${flagId}/scopes`), {
    credentials: 'include',
  });
  
  if (!response.ok) {
    throw new Error('Failed to fetch feature flag scopes');
  }

  return response.json();
};

export const createFeatureFlagScope = async (
  flagId: number, 
  scopeType: string, 
  scopeId: string
): Promise<CreateFeatureFlagScopeResponseDto> => {
  const response = await fetch(getApiUrl(`/api/feature-flags/${flagId}/scopes`), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({ scopeType, scopeId }),
  });

  if (!response.ok) {
    throw new Error('Failed to create feature flag scope');
  }

  return response.json();
};

export const updateFeatureFlagScope = async (
  flagId: number,
  scopeType: string,
  scopeId: string,
  data: { scopeType?: string; scopeId?: string }
): Promise<UpdateFeatureFlagScopeResponseDto> => {
  const response = await fetch(getApiUrl(`/api/feature-flags/${flagId}/scopes/${scopeType}/${scopeId}`), {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error('Failed to update feature flag scope');
  }

  return response.json();
};

export const deleteFeatureFlagScope = async (
  flagId: number,
  scopeType: string,
  scopeId: string
): Promise<DeleteFeatureFlagScopeResponseDto> => {
  const response = await fetch(getApiUrl(`/api/feature-flags/${flagId}/scopes/${scopeType}/${scopeId}`), {
    method: 'DELETE',
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to delete feature flag scope');
  }

  return response.json();
}; 